import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {generateMarkers} from "../../helpers/helpers";
import {useObservationImages} from "../../helpers/useObservationImages";
import {usePageTitle} from "../../helpers/usePageTitle";
import MapView from "./map-view";
import {MapMode} from "./constants";
import type {MapExplorerProps, MapModeType} from "./types";

export default function MapExplorer({
    observations,
    checklist,
    year,
    month,
    homeLocation,
    observationsByLocation,
    onStartBigYear,
    onShowTimePeriodSelector,
    mapMode,
    mapTour,
    activeSpecies = 0,
    speciesImages,
    getSpeciesImages,
    lookupByTaxonomicOrder,
    familyByTaxonomicOrder,
    parentTaxonomicOrderByTaxonomicOrder
}: MapExplorerProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [markers, setMarkers] = useState<any>();
    const [activeLocation, setActiveLocation] = useState<any>();
    usePageTitle('Map');
    const getImageTaxonomicOrder = useCallback((taxonomicOrder: number) => {
        return parentTaxonomicOrderByTaxonomicOrder.get(taxonomicOrder) ?? taxonomicOrder;
    }, [parentTaxonomicOrderByTaxonomicOrder]);
    const { setObservationImages, setObservationImagesForMarkers } = useObservationImages(
        speciesImages,
        lookupByTaxonomicOrder,
        getImageTaxonomicOrder
    );
    const resolvedMapMode = Object.values(MapMode).includes(mapMode as MapModeType)
        ? mapMode as MapModeType
        : MapMode.Year;
    const lifersOnly = searchParams.get('lifersOnly') === 'true';
    const isLifersOnlyActive = resolvedMapMode === MapMode.Year && !mapTour && lifersOnly;
    const mapObservations = useMemo(() => {
        return isLifersOnlyActive
            ? observations.filter(observation => observation.isLifer)
            : observations;
    }, [isLifersOnlyActive, observations]);
    const speciesOptions = useMemo(() => {
        return checklist.flatMap(family => family.speciesList)
            .filter(species => species.seen)
            .map(species => ({
                commonName: species.commonName,
                scientificName: species.scientificName,
                speciesCode: species.speciesCode,
                taxonomicOrder: species.taxonomicOrder
            }));
    }, [checklist]);
    const setLifersOnly = (nextLifersOnly: boolean) => {
        const nextParams = new URLSearchParams(searchParams);

        if (nextLifersOnly) {
            nextParams.set('lifersOnly', 'true');
            nextParams.set('mode', MapMode.Year);
            nextParams.delete('tour');
            nextParams.delete('species');
        } else {
            nextParams.delete('lifersOnly');
        }

        navigate(`/map?${nextParams.toString()}`);
    };

    useEffect(() => {
        const markers = generateMarkers(mapObservations);

        getSpeciesImages(markers);
        setObservationImagesForMarkers(markers);
        setObservationImages(mapObservations);
        setMarkers(markers);

    }, [getSpeciesImages, mapObservations, setObservationImages, setObservationImagesForMarkers]);

    useEffect(() => {
        if (markers?.features?.length) {
            setObservationImagesForMarkers(markers);
        }

    }, [markers, setObservationImagesForMarkers, speciesImages]);

    if (!markers) {
        return null;
    }

    return (
        <>
            <MapView
                markers={markers}
                year={year}
                month={month}
                homeLocation={homeLocation}
                mode={resolvedMapMode}
                tourMode={mapTour}
                showPlayControls={resolvedMapMode === MapMode.Year}
                lifersOnly={lifersOnly}
                onSetLifersOnly={setLifersOnly}
                activeSpecies={activeSpecies}
                activeLocation={activeLocation}
                setActiveLocation={setActiveLocation}
                observationsByLocation={observationsByLocation}
                speciesOptions={speciesOptions}
                familyByTaxonomicOrder={familyByTaxonomicOrder}
                setObservationImages={setObservationImages}
                onShowTimePeriodSelector={onShowTimePeriodSelector}
            />
        </>
    );
}
